import React from "react";
import HttpServices from "../../utils/Http.services";
import { translate, checkTranslation } from "../../utils/useful";
import { Pie, Doughnut, Bar } from "react-chartjs-2";
import { siteTheme } from "../../variables/config";

const colors = siteTheme.primaryColors

class ProcessChartWidget extends React.Component {
    state = {
        isLoading: true,
        data: [],
        chartData: {
            labels: [],
            datasets: [{
                data: [],
                backgroundColor: [],
            }],
        },
        showLegend: false,
    }

    componentDidMount() {
        this.fetchData()
    }

    fetchData = () => {
        const widget = this.props.widget
        let body = widget.postBody || {}
        body.frame = body.frame || 'forms'
        body.limit = 1000
        body.getCount = false

        // Use fake data if needed
        if (this.props.useFakeData) {
            this.setFakeData()
            return
        }

        HttpServices.request(widget.fetchUrl, body, (fetchResult, fetchError) => {
            if (fetchError) {
                console.error('Error fetching data:', fetchError)
                this.setFakeData()
                return
            }

            const records = fetchResult?.info || []
            this.processData(records)
        })
    }

    setFakeData = () => {
        const widget = this.props.widget

        // Determine current user type for stage visibility filtering
        const currentUserType = this.props.user?.info?.type || this.props.userType || widget.userType || widget.processOwner || 'admin'

        // Filter stages based on visibleFor (if defined)
        const allStages = widget.processStages || {}
        const stages = Object.fromEntries(
            Object.entries(allStages).filter(([_, stage]) => {
                if (!stage.visibleFor || !Array.isArray(stage.visibleFor)) return true
                return stage.visibleFor.includes(currentUserType)
            })
        )
        const fakeRecords = []
        const stageKeys = Object.keys(stages)

        for (let i = 0; i < 30; i++) {
            const randomStage = stageKeys[Math.floor(Math.random() * stageKeys.length)]
            fakeRecords.push({
                _id: `fake_${i}`,
                values: {
                    currentStage: randomStage,
                },
            })
        }

        this.processData(fakeRecords)
    }

    processData = (records) => {
        const widget = this.props.widget

        // Determine current user type for stage visibility filtering
        const currentUserType = this.props.user?.info?.type || this.props.userType || widget.userType || widget.processOwner || 'admin'

        // Filter stages based on visibleFor (if defined)
        const allStages = widget.processStages || {}
        const stages = Object.fromEntries(
            Object.entries(allStages).filter(([_, stage]) => {
                if (!stage.visibleFor || !Array.isArray(stage.visibleFor)) return true
                return stage.visibleFor.includes(currentUserType)
            })
        )
        const stageCounts = {}

        Object.keys(stages).forEach(stageKey => {
            stageCounts[stageKey] = 0
        })

        records.forEach(record => {
            const stage = record.values?.currentStage || record.currentStage
            if (stage && stageCounts.hasOwnProperty(stage)) {
                stageCounts[stage]++
            }
        })

        // Modern, vibrant color palette with better contrast
        // Modern, vibrant color palette with better contrast and distinct hues
        const colorPalette = [
            '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
            '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
            '#F43F5E', '#EAB308', '#A855F7', '#D946EF', '#0EA5E9',
            '#22C55E', '#64748B', '#84CC16', '#3B82F6', '#146a5d',
            '#1a8c7a', '#20ae97', '#26d0b4', '#6366f1', '#8b5cf6',
            '#a855f7', '#c084fc', '#d8b4fe', '#3b82f6', '#60a5fa',
            '#93c5fd', '#bfdbfe', '#dbeafe', '#ec4899', '#f472b6',
            '#f9a8d4', '#fbcfe8', '#fce7f3', '#f59e0b', '#fbbf24'
        ]

        const labels = []
        const data = []
        const backgroundColor = []
        let colorIndex = 0

        Object.entries(stages).forEach(([key, stage]) => {
            const assignedColor = stage.color || colorPalette[colorIndex % colorPalette.length]
            colorIndex++

            if (stageCounts[key] > 0) {
                labels.push(checkTranslation(stage.name))
                data.push(stageCounts[key])
                backgroundColor.push(assignedColor)
            }
        })

        this.setState({
            data: records,
            chartData: {
                labels,
                datasets: [{
                    data,
                    backgroundColor,
                }],
            },
            isLoading: false,
        })
    }

    render() {
        const { chartData, isLoading } = this.state
        const chartType = this.props.widget.chartType || 'pie'

        if (isLoading) {
            return (
                <div className="w-100 py-3 text-center">
                    <p>{translate('Loading...')}</p>
                </div>
            )
        }

        if (chartData.labels.length === 0) {
            return (
                <div className="w-100 py-3 text-center">
                    <p>{translate('No data available')}</p>
                </div>
            )
        }

        const ChartComponent = chartType === 'bar' ? Bar : (chartType === 'doughnut' ? Doughnut : Pie)

        return (
            <div className="w-100 py-2">
                <div className="d-flex justify-content-end mb-2">
                    <button
                        onClick={() => this.setState({ showLegend: !this.state.showLegend })}
                        style={{
                            background: this.state.showLegend
                                ? `linear-gradient(135deg, ${colors.main1} 0%, ${colors.main1} 75%, ${colors.main2} 100%)`
                                : `linear-gradient(135deg, ${colors.main3}50, ${colors.main3}60)`,
                            border: `2px solid ${this.state.showLegend ? colors.main1 : colors.main3 + '80'}`,
                            borderRadius: 20,
                            padding: '6px 16px',
                            fontSize: 11,
                            fontWeight: 600,
                            color: this.state.showLegend ? '#fff' : colors.main1,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: this.state.showLegend ? `0 2px 8px ${colors.main1}30` : 'none'
                        }}
                    >
                        {this.state.showLegend ? translate('Hide Legend') : translate('Show Legend')}
                    </button>
                </div>
                <div style={{ padding: '20px', overflow: 'hidden', position: 'relative' }}>
                    <div className="p-3" style={{
                        background: '#fff',
                        minHeight: 400,
                        borderRadius: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        overflow: 'visible'
                    }}>
                        <ChartComponent
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                cutout: chartType === 'doughnut' ? '25%' : undefined,
                                layout: {
                                    padding: {
                                        left: 0,
                                        right: 0,
                                        top: 0,
                                        bottom: this.state.showLegend ? 50 : 0
                                    }
                                },
                                plugins: {
                                    legend: {
                                        display: this.state.showLegend,
                                        position: 'bottom',
                                        align: 'center',
                                        labels: {
                                            boxWidth: 14,
                                            padding: 10,
                                            font: { size: 11, weight: '500' },
                                            usePointStyle: true,
                                            pointStyle: 'circle',
                                            generateLabels: function (chart) {
                                                const data = chart.data
                                                if (data.labels.length && data.datasets.length) {
                                                    return data.labels.map((label, i) => {
                                                        const value = data.datasets[0].data[i]
                                                        const color = 'data.datasets[0].backgroundColor[i]'
                                                        const total = data.datasets[0].data.reduce((a, b) => a + b, 0)
                                                        const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                        return {
                                                            text: `${label}: ${value} (${percentage}%)`,
                                                            fillStyle: color,
                                                            strokeStyle: color,
                                                            lineWidth: 0,
                                                            hidden: false,
                                                            index: i
                                                        }
                                                    })
                                                }
                                                return []
                                            }
                                        }
                                    },
                                    datalabels: {
                                        display: false
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context) {
                                                const label = context.label || ''
                                                const value = context.parsed || context.raw
                                                const total = context.dataset.data.reduce((a, b) => a + b, 0)
                                                const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                return `${label}: ${value} (${percentage}%)`
                                            }
                                        }
                                    }
                                },
                                elements: {
                                    arc: {
                                        borderWidth: 2,
                                        borderColor: '#fff'
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        )
    }
}

export default ProcessChartWidget

